import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpRequest } from '@angular/common/http';
import { LoggerService } from './logger.service';
import { appConfig, AuthGaurdService } from './auth-gaurd.service';
import { Observable, throwError } from 'rxjs';
import { MessageModel } from '../models/commission/message.model';
import { environment } from '../../environments/environment';
import { NotificationService } from './notification.service';
import { catchError, tap } from 'rxjs/operators';
import { MessageNotificationConfig } from '../models/commission/message-notification.model';

@Injectable({
  providedIn: 'root',
})
export class MessageService {
  host: string = environment.apiUrl + 'user-notifications';
  notificationConfigHost: string = environment.apiUrl + 'notification-configs';
  contextTemplateHost: string = environment.apiUrl + 'context-templates';
  tokenId: string = '';
  messages: MessageModel[] = [];
  loggedinUser: string = '';
  loggedInUserEmpId: string = '';

  constructor(
    private http: HttpClient,
    private logger: LoggerService,
    private authgurdService: AuthGaurdService,
    private authGaurdService: AuthGaurdService
  ) {
    this.tokenId = this.authgurdService.getToken();

    this.loggedinUser = this.authGaurdService.loginUser();
    if (this.loggedinUser == '' || this.loggedinUser == null) {
      this.loggedinUser = 'NA';
    } else {
      this.loggedinUser = this.authGaurdService.loginUser();
      this.loggedInUserEmpId = this.authGaurdService.loginEmployeeId();
    }
  }

  public getMessagesByEmpId(loginEmployeeId: string): Observable<MessageModel[]> {
    console.log('logged in user: ' + loginEmployeeId);
    return this.http.get<MessageModel[]>(
      `${this.host}/employee` + '/' + loginEmployeeId
    );
  }

  // POST
  public createNotificationConfig(cfg: MessageNotificationConfig): Observable<MessageNotificationConfig> {
    return this.http.post<MessageNotificationConfig>(
      this.notificationConfigHost,
      cfg,
      appConfig.httpOptions
    ).pipe(
      tap((savedConfig: MessageNotificationConfig) => {
        // Handle Flask trigger decision here
        this.triggerFlaskIfNeeded(savedConfig);
      }),
      catchError(this.handleError)
    );
  }

  private triggerFlaskIfNeeded(cfg: MessageNotificationConfig) {
    const now = new Date();
    const createdAt = new Date(cfg.createdAt);
    let startAt: Date | null = cfg.startAt ? new Date(cfg.startAt) : null;

    if (cfg.cronExpression && cfg.cronExpression.trim().length > 0) {
      const derived = this.deriveStartAtFromCron5(cfg.cronExpression.trim(), now);
      if (derived) startAt = derived;
    }

    const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getDate(), 10, 0, 0));
    const nextBatch = new Date(today.getTime() + 24 * 60 * 60 * 1000);

    //  Trigger Flask if: created after today's 10AM UTC - startAt before next batch (tomorrow 10AM UTC)
    
    if (
      // Recurring: trigger immediately (no 10AM window), but only if not already triggered
      (cfg.cronExpression && cfg.cronExpression.trim().length > 0 && !cfg.lastTriggeredAt)
      ||
      // One-time: keep existing 10AM→next batch window rule
      (createdAt > today && !cfg.cronExpression && startAt! < nextBatch && !cfg.lastTriggeredAt)
    ) {
      // HOA-8442: route through the Java backend proxy (trusted *.run.app origin) instead of
      // the engine's private IP. No engine credential is needed for this system trigger.
      const url = `${environment.apiUrl}engine/messages/generate`;
this.http.post(url, /* payload */ {}).subscribe({
  next: res => {
    console.log('✅ Flask trigger success:', res);

    // ⬇️ STEP 3: call backend cleanup AFTER Flask has inserted the new row
    this.http.post(
      `${this.notificationConfigHost}/${cfg.notifConfigId}/cleanup-future`,
      {}
    ).subscribe({
      next: () => console.log('🧹 cleanup-future ok'),
      error: e => console.error('❌ cleanup-future failed', e)
    });
  },
  error: err => console.error('❌ Flask trigger failed:', err)
});
    } else {
      console.log('⏳ Flask trigger skipped — outside time window');
    }
  }
  
  private deriveStartAtFromCron5(cron: string, now: Date = new Date()): Date | null {
    const parts = cron.split(/\s+/);
    if (parts.length !== 5) return null;

    const [mm, HH, dom, mon, dow] = parts;

    // Pattern A: "mm HH DD M/interval ?"  (allow ? or * in DOW slot)
    const isDom =
      /^\d{1,2}$/.test(mm) &&
      /^\d{1,2}$/.test(HH) &&
      /^\d{1,2}$/.test(dom) &&
      /^[*\d/]+$/.test(mon) &&
      (/^\?$|^\*$/.test(dow));
    if (isDom) {
      const minute = this.clampInt(mm, 0, 59);
      const hour   = this.clampInt(HH, 0, 23);
      const day    = this.clampInt(dom, 1, 31);
      const { startMonth, interval } = this.parseMonthField(mon);
      return this.nextDomOccurrence(now, { hour, minute, day, startMonth, interval });
    }

    // Pattern B: "mm HH ? * DDD#N" (also accept "*" instead of "?")
    const isNthDow =
      /^\d{1,2}$/.test(mm) &&
      /^\d{1,2}$/.test(HH) &&
      (/^\?$|^\*$/.test(dom)) &&
      mon === '*' &&
      /^(SUN|MON|TUE|WED|THU|FRI|SAT)#([1-5])$/i.test(dow);
    if (isNthDow) {
      const minute = this.clampInt(mm, 0, 59);
      const hour   = this.clampInt(HH, 0, 23);
      const [, ddd, nthStr] = dow.toUpperCase().match(/^([A-Z]{3})#([1-5])$/)!;
      const nth = parseInt(nthStr, 10);
      const weekday = this.dowToNum(ddd);
      return this.nextNthWeekdayOccurrence(now, { hour, minute, weekday, nth });
    }

    return null;
  }
  private nextNthWeekdayOccurrence(
    now: Date,
    opt: { hour: number; minute: number; weekday: number; nth: number }
  ): Date {
    const { hour, minute, weekday, nth } = opt;

    // Target in THIS month
    const y = now.getFullYear();
    const m1 = now.getMonth() + 1; // 1..12
    const targetThis = this.nthWeekdayOfMonth(y, m1, weekday, nth);
    const candidateThis = new Date(
      targetThis.getFullYear(), targetThis.getMonth(), targetThis.getDate(),
      hour, minute, 0, 0
    );

    if (now.getTime() <= candidateThis.getTime()) {
      return candidateThis; // e.g., for WED#3 in Nov 2025 -> Nov 19, 11:57 if we're before that moment
    }

    // Otherwise NEXT month
    let yNext = y;
    let m1Next = m1 + 1;
    if (m1Next === 13) { yNext = y + 1; m1Next = 1; }

    const targetNext = this.nthWeekdayOfMonth(yNext, m1Next, weekday, nth);
    const candidateNext = new Date(
      targetNext.getFullYear(), targetNext.getMonth(), targetNext.getDate(),
      hour, minute, 0, 0
    );
    return candidateNext;
  }
  private nthWeekdayOfMonth(year: number, month1: number, weekday: number, n: number): Date {
    const first = new Date(year, month1 - 1, 1);
    const firstDow = first.getDay();
    const offset = (7 + weekday - firstDow) % 7;
    const day = 1 + offset + (n - 1) * 7;
    const lastDay = new Date(year, month1, 0).getDate();
    return new Date(year, month1 - 1, Math.min(day, lastDay));
  }
  private nthOfWeekInMonth(d: Date): number {
    return Math.floor((d.getDate() - 1) / 7) + 1; // 1..5
  }
  private nextDomOccurrence(
    now: Date,
    opt: { hour: number; minute: number; day: number; startMonth: number; interval: number }
  ): Date {
    const { hour, minute, day, startMonth, interval } = opt;

    const currentYear = now.getFullYear();
    const currentMonthIdx = now.getMonth();       // 0..11
    const currentMonth1 = currentMonthIdx + 1;    // 1..12

    // Is this month on the cadence?
    const onCadence = (((currentMonth1 - startMonth) % interval) + interval) % interval === 0;

    // Candidate in THIS month (clamp for short months)
    const lastDayThisMonth = new Date(currentYear, currentMonthIdx + 1, 0).getDate();
    const domThis = Math.min(day, lastDayThisMonth);
    const candidateThis = new Date(currentYear, currentMonthIdx, domThis, hour, minute, 0, 0);

    // Use this month if cadence matches and we haven't passed the time yet
    if (onCadence && now.getTime() <= candidateThis.getTime()) {
      return candidateThis;
    }

    // Otherwise advance to the next month that matches the cadence
    let m1 = currentMonth1 + 1;
    while (((((m1 - startMonth) % interval) + interval) % interval) !== 0) m1++;
    const year = currentYear + Math.floor((m1 - 1) / 12);
    const monthIdx = (m1 - 1) % 12;

    const lastDayNext = new Date(year, monthIdx + 1, 0).getDate();
    const domNext = Math.min(day, lastDayNext);
    return new Date(year, monthIdx, domNext, hour, minute, 0, 0);
  }
  private parseMonthField(field: string): { startMonth: number; interval: number } {
    // accepts "*/N", "M/N", or "M"
    if (field.includes('/')) {
      const [start, every] = field.split('/');
      const interval = Math.max(1, this.clampInt(every, 1, 12));
      if (start === '*' || start === '' || start === '*/') return { startMonth: 1, interval }; // Jan baseline
      return { startMonth: this.clampInt(start, 1, 12), interval };
    }
    const m = this.clampInt(field, 1, 12);
    return { startMonth: m, interval: 12 }; // once a year at that month
  }
  private dowToNum(ddd: string): number {
    const map: Record<string, number> = { SUN:0, MON:1, TUE:2, WED:3, THU:4, FRI:5, SAT:6 };
    return map[ddd.toUpperCase()];
  }
 
  private clampInt(s: string | number, min: number, max: number): number {
    const n = typeof s === 'number' ? s : parseInt(s as string, 10);
    return Math.min(max, Math.max(min, isNaN(n) ? min : n));
  }

  public getNotificationConfigsByEmpId(): Observable<
    MessageNotificationConfig[]
  > {
    return this.http.get<MessageNotificationConfig[]>(
      `${this.notificationConfigHost}?empId=${this.loggedInUserEmpId}`
    );
  }  

  public dismissMessage(id: string, empId: String): Observable<void> {
    console.log('dismissMessage id: ' + id);
    return this.http
      .put<void>(`${this.host}/${empId}/${id}/dismiss`, {}, appConfig.httpOptions)
      .pipe(catchError(this.handleError));
  }

  public handleError(error: {
    error: { message: string };
    status: any;
    message: any;
  }) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      errorMessage = error.error.message;
    } else {
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    window.alert(errorMessage);
    return throwError(errorMessage);
  }
  public getNotificationConfigById(
    id: string
  ): Observable<MessageNotificationConfig> {
    console.log('Fetching config by id: ' + id);
    return this.http.get<MessageNotificationConfig>(
      `${this.notificationConfigHost}/${id}`
    );
  }

  public updateNotificationConfig(
    id: string,
    cfg: MessageNotificationConfig
  ): Observable<MessageNotificationConfig> {
    return this.http.put<MessageNotificationConfig>(
      `${this.notificationConfigHost}/${id}`,
      cfg,
      appConfig.httpOptions
    ).pipe(
      tap((savedConfig: MessageNotificationConfig) => {
        // Handle Flask trigger decision here
        this.triggerFlaskIfNeeded(savedConfig);
      }),
      catchError(this.handleError)
    );
  }

  // ===================== Context Templates API =====================

  public getContextTemplates(): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.contextTemplateHost}`,
      appConfig.httpOptions
    ).pipe(catchError(this.handleError));
  }

  public createContextTemplate(payload: { templateName: string; messageContent: any }): Observable<any> {
    return this.http.post<any>(
      `${this.contextTemplateHost}`,
      payload,
      appConfig.httpOptions
    ).pipe(catchError(this.handleError));
  }

  public softDeleteContextTemplate(id: string): Observable<void> {
    return this.http.delete<void>(
      `${this.contextTemplateHost}/${id}`,
      appConfig.httpOptions
    ).pipe(catchError(this.handleError));
  }
  

}